import { Fragment } from "react";
import { Box, Grid, MenuItem, TextField, Typography } from "@mui/material";
import CountryCodeSelect from "../../common/country-code-select";
import typography from "../../cssUtils/typography";
import { ColorsConstants } from "../../cssUtils/constants";
import { FIELD_LABELS, SALUTATIONS } from "./membersUtils";

const FIELD_KINDS = {
  salutation: "salutation",
  membership_class: "class",
  mobile: "mobile",
  dob: "date",
  anniversary: "date",
  registration_date: "date",
  membership_end_date: "date",
  services_to_date: "date",
  icai_date: "date",
  email: "email",
};

const LABEL_OVERRIDES = { mobile: "Mobile", membership_end_date: "Valid till (blank = Life)" };
const PLACEHOLDERS = { membership_id: "e.g. O-032500" };

/**
 * The member fields, laid out in `sections` ([{ title?, fields: [key, ...] }]).
 * Shared by the Add member modal and Edit details so both validate and read alike.
 *
 * values / errors are keyed like EMPTY_MEMBER_VALUES; `mobile` renders as two cells of the grid:
 * the country code dropdown, then the number. Pass `readOnlyKeys` for fields that cannot change (Membership ID
 * on edit) and `fieldRefs` (a ref holding {}) so the parent can focus the first invalid field.
 */
const MemberForm = ({
  values,
  errors = {},
  onChange,
  onBlurField,
  sections,
  classes = [],
  readOnlyKeys = [],
  fieldRefs,
}) => {
  const commonProps = (key) => ({
    size: "small",
    fullWidth: true,
    label: LABEL_OVERRIDES[key] || FIELD_LABELS[key] || key,
    value: values[key] ?? "",
    error: Boolean(errors[key]),
    helperText: errors[key] || " ",
    onChange: (event) => onChange(key, event.target.value),
    onBlur: () => onBlurField?.(key),
    disabled: readOnlyKeys.includes(key),
    inputRef: (node) => {
      if (fieldRefs?.current) fieldRefs.current[key] = node;
    },
  });

  const renderField = (key) => {
    switch (FIELD_KINDS[key]) {
      case "salutation":
        return (
          <TextField {...commonProps(key)} select>
            <MenuItem value="">None</MenuItem>
            {SALUTATIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        );
      case "class":
        return (
          <TextField {...commonProps(key)} select>
            <MenuItem value="">None</MenuItem>
            {classes.map((option) => (
              <MenuItem key={option.id} value={String(option.id)}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        );
      case "date":
        return <TextField {...commonProps(key)} type="date" InputLabelProps={{ shrink: true }} />;
      case "email":
        return <TextField {...commonProps(key)} type="email" />;
      case "mobile":
        return (
          <TextField
            {...commonProps(key)}
            required
            type="tel"
            inputProps={{ inputMode: "tel", "aria-label": "Mobile" }}
          />
        );
      default:
        return <TextField {...commonProps(key)} placeholder={PLACEHOLDERS[key]} />;
    }
  };

  return (
    <Box>
      {sections.map((section, index) => (
        <Box key={section.title || index} sx={{ mb: 1 }}>
          {section.title && (
            <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: ColorsConstants.primaryBlue100, mb: 1.5 }}>
              {section.title}
            </Typography>
          )}
          <Grid container columnSpacing={2} rowSpacing={1}>
            {section.fields.map((key) =>
              key === "mobile" ? (
                <Fragment key={key}>
                  <Grid item xs={12} sm={6}>
                    <CountryCodeSelect
                      size="small"
                      label="Country code"
                      value={values.country_iso}
                      onChange={(iso) => onChange("country_iso", iso)}
                      sx={{ width: "100%" }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {renderField(key)}
                  </Grid>
                </Fragment>
              ) : (
                <Grid item xs={12} sm={6} key={key}>
                  {renderField(key)}
                </Grid>
              ),
            )}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};

export default MemberForm;
